import { DestroyRef, Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Auth, User as FirebaseUser, onIdTokenChanged, signOut } from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthClientService {
  // equal: () => false - Firebase mutates the same User reference in-place after
  // updateProfile, so identity-equality would swallow the change. Force every
  // publishUser to notify so dependent computeds (displayName, initial,
  // avatarUrl) re-fire on token refresh.
  readonly currentUser: WritableSignal<FirebaseUser | null> = signal<FirebaseUser | null>(null, {
    equal: (): boolean => false
  });

  private readonly authedSubject$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly currentUserSubject$: ReplaySubject<FirebaseUser | null> = new ReplaySubject<FirebaseUser | null>(1);

  readonly isAuthed$: Observable<boolean> = this.authedSubject$.asObservable();
  readonly currentUser$: Observable<FirebaseUser | null> = this.currentUserSubject$.asObservable();

  private readonly auth: Auth = inject(Auth);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    this.subscribeToAuthState();
  }

  async getIdToken(forceRefresh: boolean = false): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const result: { token: string } = await FirebaseAuthentication.getIdToken({ forceRefresh });
      return result.token ?? null;
    }
    const user: FirebaseUser | null = this.auth.currentUser;
    return user ? user.getIdToken(forceRefresh) : null;
  }

  async signOut(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signOut();
      return;
    }
    await signOut(this.auth);
  }

  private subscribeToAuthState(): void {
    if (Capacitor.isNativePlatform()) {
      this.subscribeNativeAuthState();
      return;
    }
    // Use onIdTokenChanged (not authState) so we also re-publish on forced token
    // refresh - covers profile edits (updateProfile + getIdToken(true)) so the
    // signal gets a new reference and computeds (displayName, initial, avatarUrl)
    // re-fire. Without this, mutated currentUser fields are invisible to UI.
    const unsubscribe: () => void = onIdTokenChanged(this.auth, (user: FirebaseUser | null): void => {
      this.publishUser(user);
    });
    this.destroyRef.onDestroy(unsubscribe);
  }

  private subscribeNativeAuthState(): void {
    void FirebaseAuthentication.getCurrentUser().then(({ user }: { user: unknown }): void =>
      this.publishUser(user as FirebaseUser | null)
    );
    void FirebaseAuthentication.addListener('authStateChange', ({ user }: { user: unknown }): void =>
      this.publishUser(user as FirebaseUser | null)
    );
  }

  private publishUser(user: FirebaseUser | null): void {
    this.currentUser.set(user);
    this.currentUserSubject$.next(user);
    this.authedSubject$.next(user !== null);
  }
}
